import { useState, useEffect, useCallback } from 'react'
import { useParams, useLocation, Link } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { useAuth } from '@/hooks/useAuth'
import { puedeVerRuta } from '@/lib/modulos'
import { useCachedQuery } from '@/hooks/useCachedQuery'
import { formatCurrency, r2 } from '@/lib/utils'
import StatCard from '@/components/shared/StatCard'
import TabBar from '@/components/shared/TabBar'
import TabResumen from '@/components/empresa/TabResumen'
import TabCotizaciones from '@/components/empresa/TabCotizaciones'
import TabProyectos from '@/components/empresa/TabProyectos'
import TabFacturacion from '@/components/empresa/TabFacturacion'
import TabContactos from '@/components/empresa/TabContactos'
import type { ContactoConRoles } from '@/components/empresa/TabContactos'
import TabSoporte from '@/components/empresa/TabSoporte'
import TabConfiguracion from '@/components/empresa/TabConfiguracion'
import { PageHeader } from '@/components/ui'
import {
  IconEdit, IconDollar, IconClock,
  IconTarget, IconHeadset, IconFileText, IconReceipt,
  IconUser, IconBarChart, IconBuilding,
} from '@/components/icons/Icons'
import type {
  Cliente, Cotizacion, Proyecto, Factura, Tarea,
  ConfigTarifaCliente, PagoProgramado, RolContacto,
} from '@/lib/types'

const TABS = [
  { key: 'resumen', label: 'Resumen', icon: <IconBarChart size={15} /> },
  { key: 'cotizaciones', label: 'Cotizaciones', icon: <IconFileText size={15} /> },
  { key: 'proyectos', label: 'Proyectos', icon: <IconTarget size={15} /> },
  { key: 'facturacion', label: 'Facturacion', icon: <IconReceipt size={15} /> },
  { key: 'contactos', label: 'Contactos', icon: <IconUser size={15} /> },
  { key: 'soporte', label: 'Soporte', icon: <IconHeadset size={15} /> },
  { key: 'configuracion', label: 'Configuracion', icon: <IconBuilding size={15} /> },
]

/*
  Lo que el partner NO ve de una empresa de su cartera: Facturacion y Soporte
  son lo que 1Conecta le factura y la tarifa por hora con que se lo medimos, y
  Configuracion es donde esa tarifa se edita. Son numeros nuestros sobre su
  cliente: no es que le falte permiso, es que no son suyos.
*/
const TABS_PARTNER = new Set(['resumen', 'cotizaciones', 'proyectos', 'contactos'])

export default function VerEmpresa() {
  const { id } = useParams<{ id: string }>()
  const api = useApi()
  const enPartner = useLocation().pathname.startsWith('/partner')
  const rutaEmpresas = enPartner ? '/partner/empresas' : '/clientes'

  /*
    Qué partes de la ficha puede pedir esta sesión.

    El partner ya tenía su recorte, escrito a mano. Una sesión recortada por
    módulos (ventas) necesita otro, y sale de los mismos módulos que arman el
    menú — así la ficha no puede ofrecer una pestaña cuya API le va a devolver
    403. Un admin tiene `modulos` nulo y las dos salen verdaderas.
  */
  const { modulos } = useAuth()
  const veProyectos = puedeVerRuta(modulos, '/proyectos')
  const veFinanzas = !enPartner && puedeVerRuta(modulos, '/facturas')

  const tabs = TABS.filter(t => {
    if (enPartner && !TABS_PARTNER.has(t.key)) return false
    if (t.key === 'proyectos') return veProyectos
    // Facturación son nuestras facturas; Soporte y Configuración viven de la
    // tarifa por hora, que es un dato de admin.
    if (t.key === 'facturacion' || t.key === 'soporte' || t.key === 'configuracion') return veFinanzas
    return true
  })
  const { data: clientes = [], isLoading } = useCachedQuery<Cliente>('clientes')

  const [activeTab, setActiveTab] = useState('resumen')
  const [contactos, setContactos] = useState<ContactoConRoles[]>([])
  const [cotizaciones, setCotizaciones] = useState<Cotizacion[]>([])
  const [proyectos, setProyectos] = useState<Proyecto[]>([])
  const [facturas, setFacturas] = useState<Factura[]>([])
  const [tarifa, setTarifa] = useState<ConfigTarifaCliente | null>(null)
  const [proximoPago, setProximoPago] = useState<PagoProgramado | null>(null)
  const [tareasSoporte, setTareasSoporte] = useState<Tarea[]>([])
  const [dataLoaded, setDataLoaded] = useState(false)

  const empresa = clientes.find(c => c.id === Number(id))
  const clienteId = Number(id)

  const loadData = useCallback(async (cid: number) => {
    // Load all data in parallel. allSettled so a single failed request
    // doesn't abort the whole load and leave the page stuck on "Cargando...".
    // En el espacio del partner ni se piden las tres de dinero: dos son de
    // admin (tarifa y pagos programados) y la tercera son nuestras facturas a
    // su cliente. Pedirlas igual dejaba un 403 en consola por cada visita.
    const vacio = async () => [] as never[]
    const settled = await Promise.allSettled([
      api.get<Cotizacion[]>('/api/cotizaciones'),
      veProyectos ? api.get<Proyecto[]>('/api/proyectos') : vacio(),
      veFinanzas ? api.get<Factura[]>('/api/facturas') : vacio(),
      veFinanzas ? api.get<ConfigTarifaCliente[]>('/api/config/tarifa-cliente') : vacio(),
      api.get(`/api/contactos/cliente/${cid}`),
      veFinanzas ? api.get<PagoProgramado[]>('/api/contabilidad/pagos-programados') : vacio(),
    ])
    const val = <T,>(i: number, fallback: T): T =>
      settled[i].status === 'fulfilled' ? (settled[i] as PromiseFulfilledResult<T>).value : fallback
    const cotRes = val<Cotizacion[]>(0, [])
    const projRes = val<Proyecto[]>(1, [])
    const factRes = val<Factura[]>(2, [])
    const tarifaRes = val<ConfigTarifaCliente[]>(3, [])
    const contactosRes = val<unknown>(4, [])
    const pagosRes = val<PagoProgramado[]>(5, [])

    // Filter cotizaciones for this client
    setCotizaciones(cotRes.filter(c => c.cliente_id === cid))

    // Filter proyectos for this client
    setProyectos(projRes.filter(p => p.cliente_id === cid))

    // Filter facturas for this client
    setFacturas(factRes.filter(f => f.cliente_id === cid))

    // Set tarifa
    const found = tarifaRes.find(t => t.cliente_id === cid)
    setTarifa(found || null)

    /*
      La pestana Contactos venia vacia SIEMPRE, y por dos errores encadenados:
      se pedia `/api/contactos/:id/empresas` pasandole el id del CLIENTE (esa
      ruta espera el de un CONTACTO) y luego se leia `ce.contacto`, un campo
      que esa ruta no devuelve — solo filas planas de `contacto_empresa`. La
      que corresponde es `/api/contactos/cliente/:clienteId`, que trae el join
      bajo las claves `contacto_empresa` y `contactos`.
    */
    type FilaVinculo = {
      contacto_empresa?: { roles?: RolContacto[]; cargo_empresa?: string | null }
      contactos?: {
        id?: number; nombre?: string; apellido?: string | null; email?: string | null
        telefono?: string | null; cargo?: string | null; auth_user_id?: string | null
        portal_activo?: boolean; onboarding_completado?: boolean
      } | null
    }
    const filas = Array.isArray(contactosRes) ? (contactosRes as FilaVinculo[]) : []
    setContactos(filas
      .filter(f => f.contactos?.id)
      .map(f => ({
        id: f.contactos!.id,
        nombre: f.contactos!.nombre ?? '',
        apellido: f.contactos!.apellido ?? null,
        email: f.contactos!.email ?? null,
        telefono: f.contactos!.telefono ?? null,
        cargo: f.contacto_empresa?.cargo_empresa ?? f.contactos!.cargo ?? null,
        roles: f.contacto_empresa?.roles ?? [],
        es_contabilidad: false,
        auth_user_id: f.contactos!.auth_user_id ?? null,
        portal_activo: f.contactos!.portal_activo ?? false,
        onboarding_completado: f.contactos!.onboarding_completado ?? false,
      })))

    // Find next pending payment for this client
    const hoy = new Date().toISOString().split('T')[0]
    const proximoPagoFound = pagosRes
      .filter(p => p.cliente_id === cid && p.estado === 'pendiente' && p.tipo === 'ingreso' && p.fecha_vencimiento >= hoy)
      .sort((a, b) => a.fecha_vencimiento.localeCompare(b.fecha_vencimiento))[0] || null
    setProximoPago(proximoPagoFound)

    // Load soporte tasks (tipo_cobro = 'por_horas', completed) for this client's projects
    const clienteProyectoIds = projRes.filter(p => p.cliente_id === cid).map(p => p.id)
    if (clienteProyectoIds.length > 0 && veFinanzas) {
      const allTareas = await api.get<Tarea[]>('/api/tareas')
      const tareasHoras = allTareas.filter(
        t => t.proyecto_id != null && clienteProyectoIds.includes(t.proyecto_id) && t.tipo_cobro === 'por_horas'
      )
      setTareasSoporte(tareasHoras)
    } else {
      setTareasSoporte([])
    }

    setDataLoaded(true)
  }, [api, enPartner, veProyectos, veFinanzas])

  useEffect(() => {
    if (!id) return
    loadData(Number(id))
  }, [id, loadData])

  // Computed stats
  const revenuePagada = r2(facturas.filter(f => f.estado === 'pagada').reduce((s, f) => s + f.total, 0))
  const pendienteCobro = r2(facturas.filter(f => f.estado === 'emitida' || f.estado === 'pre_factura').reduce((s, f) => s + f.total, 0))
  const proyectosActivos = proyectos.filter(p => p.estado === 'activo').length
  const horasUsadas = r2(tareasSoporte.reduce((s, t) => s + (t.horas_reales || 0), 0))
  const horasLabel = tarifa
    ? `${horasUsadas} / ${tarifa.umbral_horas_mes}h`
    : 'N/A'

  if (isLoading || !empresa) {
    return (
      <div className="page-enter" style={{ padding: '2rem' }}>
        <PageHeader breadcrumbs={[{ label: 'Empresas', to: rutaEmpresas }, { label: 'Empresa' }]} />
        <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
          {isLoading ? <><div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...</> : 'Empresa no encontrada'}
        </div>
      </div>
    )
  }

  return (
    <div className="page-enter">
      <PageHeader
        breadcrumbs={[{ label: 'Empresas', to: rutaEmpresas }, { label: empresa.empresa }]}
        meta={<span className="badge badge-activo">Activo</span>}
        subtitle={`${empresa.tipo_documento_fiscal || 'RUC'} ${empresa.ruc_dni || '---'}${empresa.direccion_fiscal ? ` · ${empresa.direccion_fiscal}` : ''}`}
        actions={
          <Link to={rutaEmpresas} className="btn btn-secondary" id="btn-edit-empresa">
            <IconEdit size={16} /> Editar
          </Link>
        }
      />

      {/* ─── Stats Row ─── */}
      {dataLoaded && (
        <div className="empresa-360-stats">
          {veFinanzas && (
            <>
              <StatCard
                icon={<IconDollar size={20} />}
                label="Revenue Total"
                value={formatCurrency(revenuePagada, 'USD')}
                color="success"
              />
              <StatCard
                icon={<IconClock size={20} />}
                label="Pendiente Cobro"
                value={formatCurrency(pendienteCobro, 'USD')}
                color="warning"
              />
            </>
          )}
          {!veFinanzas && (
            <>
              <StatCard
                icon={<IconFileText size={20} />}
                label="Cotizaciones"
                value={cotizaciones.length}
                color="info"
              />
              <StatCard
                icon={<IconUser size={20} />}
                label="Contactos"
                value={contactos.length}
                color="primary"
              />
            </>
          )}
          {veProyectos && (
            <StatCard
              icon={<IconTarget size={20} />}
              label="Proyectos Activos"
              value={proyectosActivos}
              color="info"
            />
          )}
          {veFinanzas && (
            <StatCard
              icon={<IconHeadset size={20} />}
              label="Horas Soporte"
              value={horasLabel}
              color="primary"
            />
          )}
        </div>
      )}

      {/* ─── Tabs ─── */}
      <TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* ─── Tab Content ─── */}
      <div className="empresa-360-content">
        {!dataLoaded ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
            <span style={{ color: 'var(--text-secondary)' }}>Cargando datos...</span>
          </div>
        ) : (
          <>
            {activeTab === 'resumen' && (
              <TabResumen
                empresa={empresa}
                cotizaciones={cotizaciones}
                proyectos={proyectos}
                facturas={facturas}
                proximoPago={proximoPago}
                mostrarDinero={veFinanzas}
              />
            )}
            {activeTab === 'cotizaciones' && (
              <TabCotizaciones cotizaciones={cotizaciones} empresaId={clienteId} />
            )}
            {activeTab === 'proyectos' && (
              <TabProyectos proyectos={proyectos} empresaId={clienteId} />
            )}
            {activeTab === 'facturacion' && (
              <TabFacturacion facturas={facturas} empresaId={clienteId} />
            )}
            {activeTab === 'contactos' && (
              <TabContactos
                contactos={contactos}
                empresaId={clienteId}
                empresaNombre={empresa.empresa}
                onRefresh={() => loadData(clienteId)}
                // Dar de alta en el portal crea una identidad en `usuarios`:
                // es administración de accesos, y va por `/api/auth/register`,
                // que es solo-admin.
                permiteAltaPortal={veFinanzas}
              />
            )}
            {activeTab === 'soporte' && (
              <TabSoporte
                empresaId={clienteId}
                tarifaConfig={tarifa}
                tareasSoporte={tareasSoporte}
              />
            )}
            {activeTab === 'configuracion' && (
              <TabConfiguracion
                empresa={empresa}
                tarifaConfig={tarifa}
                onUpdate={() => loadData(clienteId)}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
